import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import NewsletterForm from '@/components/NewsletterForm';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InsightDetailPage() {
  const { slug } = useParams();
  const [insight, setInsight] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | found | notfound

  useEffect(() => {
    let active = true;
    fetch(`${API}/api/content/insights/by-slug/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error('not found');
        return res.json();
      })
      .then((item) => {
        if (!active) return;
        if (item?.data?.published) {
          setInsight(item.data);
          setStatus('found');
        } else {
          setStatus('notfound');
        }
      })
      .catch(() => active && setStatus('notfound'));
    return () => { active = false; };
  }, [slug]);

  if (status === 'loading') {
    return (
      <>
        <Navigation />
        <main className="pt-32 min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin h-8 w-8 rounded-full border-b-2 border-primary" />
        </main>
        <Footer />
      </>
    );
  }

  if (status === 'notfound') {
    return (
      <>
        <SEOHead title="Insight Not Found" description="The requested insight could not be found." />
        <Navigation />
        <main className="pt-32 min-h-[60vh]" data-testid="insight-notfound">
          <div className="max-w-3xl mx-auto px-6 text-center py-24">
            <h1 className="text-4xl font-bold mb-4">Insight not found</h1>
            <p className="text-muted-foreground mb-8">The piece you're looking for may have moved or is not yet published.</p>
            <Button asChild>
              <Link to="/insights">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Insights
              </Link>
            </Button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const tags = Array.isArray(insight.tags) ? insight.tags : [];
  const canonicalUrl = `https://www.ogabajosephattah.com/insights/${slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: insight.title,
    description: insight.excerpt || insight.title,
    image: insight.cover_image ? [insight.cover_image] : undefined,
    author: { '@type': 'Person', name: insight.author || 'Ogaba Joseph Attah' },
    publisher: {
      '@type': 'Organization',
      name: 'Nexintell Limited',
      url: 'https://www.nexintell.co',
    },
    datePublished: insight.publish_date,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    keywords: tags.join(', '),
  };

  return (
    <>
      <SEOHead
        title={`${insight.title} | Insights by Ogaba Joseph Attah`}
        description={insight.excerpt || insight.title}
        canonicalUrl={canonicalUrl}
        ogImage={insight.cover_image}
        ogType="article"
        article={{
          publishedTime: insight.publish_date,
          author: insight.author || 'Ogaba Joseph Attah',
          tags,
        }}
        jsonLd={jsonLd}
      />
      <Navigation />

      <main className="pt-24 lg:pt-32" data-testid="insight-detail">
        <article className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors"
            data-testid="insight-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> All Insights
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {insight.cover_image && (
              <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl bg-muted mb-10">
                <img src={insight.cover_image} alt={insight.title} className="w-full h-full object-cover" />
              </div>
            )}

            <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight mb-6" data-testid="insight-title">
              {insight.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground mb-8 pb-8 border-b border-border">
              {insight.author && (
                <span className="inline-flex items-center gap-2"><User className="h-4 w-4" /> {insight.author}</span>
              )}
              {insight.publish_date && (
                <span className="inline-flex items-center gap-2"><Calendar className="h-4 w-4" /> {insight.publish_date}</span>
              )}
              {tags.length > 0 && (
                <span className="inline-flex items-center gap-2 flex-wrap">
                  <Tag className="h-4 w-4" />
                  {tags.map((t) => (
                    <span key={t} className="px-2 py-0.5 rounded-full bg-muted text-xs">{t}</span>
                  ))}
                </span>
              )}
            </div>

            {insight.excerpt && (
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10" data-testid="insight-excerpt">
                {insight.excerpt}
              </p>
            )}

            <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-bold prose-a:text-primary" data-testid="insight-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{insight.body || ''}</ReactMarkdown>
            </div>
          </motion.div>

          <div className="mt-16 pt-12 border-t border-border">
            <h3 className="text-xl font-semibold mb-4">Stay informed</h3>
            <p className="text-muted-foreground mb-6">Subscribe for new insights on digital infrastructure intelligence.</p>
            <NewsletterForm variant="inline" />
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}
